import { useMemo } from "react";
import { DayPicker } from "react-day-picker";
import { useQuery } from "@tanstack/react-query";
import { addDays, differenceInCalendarDays, isBefore, isSameDay, startOfDay } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { fromISO, locale, toISO } from "@/lib/format";
import { useLang } from "@/lib/i18n";

const MAX_NIGHTS = 30;
const NNBSP = "\u202f"; // narrow no-break space – prices never wrap

export function useCalendar(guests = 2) {
  const g = Math.max(1, Math.min(guests || 2, 4));
  return useQuery({
    queryKey: ["calendar", g],
    queryFn: async () => (await api.get("/calendar", { params: { days: 400, guests: g } })).data,
    staleTime: 60_000,
  });
}

export const shortPrice = (p) => (p == null ? "" : p >= 1000 ? `${Math.floor(p / 1000)}${NNBSP}${String(p % 1000).padStart(3, "0")}` : String(p));

/**
 * Range picker with nightly from-price under each date.
 * value: {ci, co} ISO strings. onChange({ci, co})
 * Selected range renders as a connected band with dark end-caps (Airbnb-style).
 */
export const DatePriceCalendar = ({ ci, co, onChange, guests = 2, months = 1, vertical = false, testid = "date-price-calendar", showLegend = true }) => {
  const { lang, tx } = useLang();
  const { data, isLoading } = useCalendar(guests);
  const today = startOfDay(new Date());
  const map = useMemo(() => {
    const m = {};
    (data?.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [data]);
  const lastDay = data?.days?.length ? fromISO(data.days[data.days.length - 1].date) : addDays(today, 365);
  const from = fromISO(ci);
  const to = fromISO(co);
  const selectingCheckout = !!from && !to;

  const firstBlocked = useMemo(() => {
    if (!from) return null;
    for (let i = 0; i <= MAX_NIGHTS; i++) {
      const d = addDays(from, i);
      const info = map[toISO(d)];
      if (info && info.available <= 0) return d;
    }
    return null;
  }, [from, map]);

  const isDisabled = (date) => {
    if (isBefore(date, today)) return true;
    if (date > addDays(lastDay, 1)) return true;
    const info = map[toISO(date)];
    if (selectingCheckout && date > from) {
      if (differenceInCalendarDays(date, from) > MAX_NIGHTS) return true;
      if (firstBlocked && date > firstBlocked) return true;
      return false;
    }
    return info ? info.available <= 0 : false;
  };

  const handleDayClick = (day, modifiers) => {
    if (modifiers.disabled) return;
    const iso = toISO(day);
    if (!from || (from && to)) return onChange({ ci: iso, co: "" });
    if (isSameDay(day, from) || isBefore(day, from)) return onChange({ ci: iso, co: "" });
    onChange({ ci: toISO(from), co: iso });
  };

  const modifiers = {
    soldout: (d) => { const i = map[toISO(d)]; return !!i && i.available <= 0 && !i.past; },
  };

  const DayContent = (props) => {
    const iso = toISO(props.date);
    const info = map[iso];
    const am = props.activeModifiers || {};
    const outside = am.outside;
    const past = isBefore(props.date, today);
    const sold = info && info.available <= 0 && !info.past;
    const isStart = !!am.range_start;
    const isEnd = !!am.range_end;
    const isMiddle = !!am.range_middle;
    const cap = (isStart || isEnd) && !am.disabled;
    const single = isStart && isEnd; // only check-in chosen so far
    const band = isMiddle ? "bg-accent" : cap && !single ? (isStart ? "bg-accent rounded-l-xl" : "bg-accent rounded-r-xl") : "";
    return (
      <span className={`flex h-full w-full items-center justify-center ${band}`} data-testid={`calendar-day-${iso}`}>
        <span className={`flex h-[46px] w-[46px] flex-col items-center justify-center rounded-md leading-none transition-colors ${cap ? "bg-foreground text-white" : ""}`}>
          <span className={`text-[0.9375rem] font-medium tnum ${sold ? "line-through" : ""}`}>{props.date.getDate()}</span>
          {!outside && !past && !sold && (
            <span className={`day-price mt-1 whitespace-nowrap text-[11px] font-medium tnum ${cap ? "text-white/75" : isMiddle ? "text-accent-foreground" : "text-muted-foreground"}`}>{shortPrice(info?.from_price)}</span>
          )}
        </span>
      </span>
    );
  };

  return (
    <div data-testid={testid} className="ffh-cal w-full min-w-0 select-none">
      <DayPicker
        mode="range"
        numberOfMonths={months}
        pagedNavigation
        weekStartsOn={1}
        locale={locale(lang)}
        fromMonth={today}
        toMonth={lastDay}
        selected={{ from, to }}
        onSelect={() => {}}
        onDayClick={handleDayClick}
        disabled={isDisabled}
        modifiers={modifiers}
        showOutsideDays={false}
        components={{
          DayContent,
          IconLeft: () => <ChevronLeft className="h-4 w-4" />,
          IconRight: () => <ChevronRight className="h-4 w-4" />,
        }}
        classNames={{
          root: "w-full min-w-0",
          months: vertical ? "flex w-full flex-col gap-8" : "flex w-full flex-col gap-8 md:flex-row md:gap-10",
          month: "w-full min-w-0 space-y-3",
          caption: "relative flex h-10 items-center justify-center",
          caption_label: "text-[0.9375rem] font-medium capitalize text-foreground",
          nav: "flex items-center",
          nav_button: "inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground transition-colors hover:bg-secondary disabled:opacity-30",
          nav_button_previous: "absolute left-0",
          nav_button_next: "absolute right-0",
          table: "w-full table-fixed border-collapse",
          head: "",
          head_row: "",
          head_cell: "pb-2 text-center text-xs font-medium text-muted-foreground",
          tbody: "",
          row: "",
          cell: "p-0 align-top",
          day: "relative flex h-[52px] w-full items-center justify-center rounded-md p-0 text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          day_selected: "!rounded-none hover:!bg-transparent",
          day_range_start: "",
          day_range_end: "",
          day_range_middle: "",
          day_today: "font-medium",
          day_disabled: "!text-muted-foreground/40 hover:!bg-transparent cursor-not-allowed",
          day_outside: "invisible",
          day_hidden: "invisible",
        }}
      />
      {showLegend && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid={`${testid}-legend`}>
          {tx(`Lågaste pris per natt i kr for ${guests} ${guests === 1 ? "gjest" : "gjester"}, frukost inkludert. Gjennomstreka datoar er fulle.`, `Lowest nightly rate in NOK for ${guests} ${guests === 1 ? "guest" : "guests"}, breakfast included. Struck-through dates are sold out.`)}
          {isLoading && <span className="ml-2 animate-pulse">{tx("Hentar prisar …", "Loading prices …")}</span>}
        </p>
      )}
    </div>
  );
};
